import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import { AppDispatch } from '../../redux/store';
import { login } from '../../redux/actions/authActions';
import ApiBase from '../../api/apiBase';
import CacheService from '../../services/CacheService';

interface CustomJwtPayload {
    exp: number;
    iss: string;
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier': string;
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'?: string;
    auth_method?: string;
}

/**
 * SSO Callback Component
 * Handles the callback from Azure AD SAML authentication.
 * Extracts the JWT token from the URL and stores it for session management.
 */
export default function SSOCallback() {
    const dispatch = AppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(true);

    useEffect(() => {
        const processCallback = async () => {
            const token = searchParams.get('token');
            const errorParam = searchParams.get('error');

            if (errorParam) {
                console.error('SSO Error:', errorParam);
                setError(`SSO Authentication failed: ${decodeURIComponent(errorParam)}`);
                setProcessing(false);
                return;
            }

            if (!token) {
                setError('No authentication token received from SSO provider');
                setProcessing(false);
                return;
            }

            try {
                // Decode and validate token
                const decodedToken = jwtDecode<CustomJwtPayload>(token);

                // Check if token is expired
                const now = Date.now() / 1000;
                if (decodedToken.exp < now) {
                    setError('Authentication token has expired. Please try again.');
                    setProcessing(false);
                    return;
                }

                const userId = decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
                const email = decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || userId;
                const authMethod = decodedToken.auth_method;

                console.log('SSO Callback - Token decoded:', {
                    userId,
                    email,
                    authMethod,
                    exp: new Date(decodedToken.exp * 1000).toISOString()
                });

                // Clear previous session data
                CacheService.clearAll();

                // Store token and user info
                localStorage.setItem('authToken', token);
                localStorage.setItem('userId', userId);
                localStorage.setItem('authMethod', authMethod || 'saml2');
                CacheService.setAuthToken(token);

                // Set authorization header for API calls
                ApiBase.putAccessTokenInfo(token);

                // Dispatch login action to Redux
                dispatch(
                    login({
                        accessToken: token,
                        permissions: [],
                    })
                );

                console.log('✅ SSO Login successful, navigating to home...');

                // Navigate to home page
                setTimeout(() => {
                    navigate('/', { replace: true });
                }, 100);
            } catch (err) {
                console.error('Error processing SSO token:', err);
                setError('Failed to process authentication token. The token may be invalid or corrupted.');
                setProcessing(false);
            }
        };

        processCallback();
    }, [searchParams, dispatch, navigate]);

    // Error state - show error message with retry option
    if (error) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 2,
                    backgroundColor: '#f5f5f5',
                }}
            >
                <Alert
                    severity="error"
                    variant="outlined"
                    sx={{
                        maxWidth: 500,
                        mb: 3,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(211, 47, 47, 0.04)',
                    }}
                >
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Authentication Failed
                    </Typography>
                    <Typography variant="body2">
                        {error}
                    </Typography>
                </Alert>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/login', { replace: true })}
                        sx={{
                            backgroundColor: '#094151',
                            '&:hover': {
                                backgroundColor: '#022631',
                            },
                        }}
                    >
                        Back to Login
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            // Retry SSO login
                            window.location.href = `${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/auth/saml/login`;
                        }}
                        sx={{
                            borderColor: '#094151',
                            color: '#094151',
                            '&:hover': {
                                borderColor: '#022631',
                                backgroundColor: 'rgba(9, 65, 81, 0.04)',
                            },
                        }}
                    >
                        Try Again
                    </Button>
                </Box>
            </Box>
        );
    }

    // Processing state - show loading spinner
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                backgroundColor: '#f5f5f5',
            }}
        >
            <CircularProgress
                size={56}
                sx={{ color: '#094151' }}
            />
            <Box sx={{ textAlign: 'center' }}>
                <Typography
                    variant="h6"
                    color="textPrimary"
                    sx={{ fontWeight: 600, mb: 1 }}
                >
                    Completing SSO Authentication
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Please wait while we verify your credentials...
                </Typography>
            </Box>
        </Box>
    );
}
